import * as ft from 'funtypes'
import { Latitude, Longitude, nullable, objectWith } from '../validation.js'

/**
 * Open Charge Map POI v3, narrowed to the fields the pipeline actually reads.
 *
 * OCM is the secondary source. It contributes exactly one thing EIPA lacks:
 * declared `Voltage` and `Amps` per connection. Everything else here exists to
 * make a POI matchable against an EIPA site.
 *
 * OCM omits keys freely and returns `null` for others, so nearly everything is
 * optional-and-nullable. Extra keys are tolerated: this is a narrowing view of
 * a much larger payload, not a mirror of it. funtypes drops what it does not
 * model, so a field that is not declared here does not reach the pipeline -
 * which is why `ConnectionInfo` below is mirrored in full rather than trimmed
 * to the two numbers the merge uses.
 */

// ---------------------------------------------------------------------------
// Reference-data leaves
//
// `/v3/referencedata` publishes these as standalone lists, and `/v3/poi` with
// `compact=false` embeds the same objects inside each POI. One definition
// serves both.
// ---------------------------------------------------------------------------

/** One row of `ConnectionTypes`: OCM's connector taxonomy. */
export const OcmConnectionType = objectWith(
  {
    ID: ft.Number,
  },
  {
    Title: nullable(ft.String),
    FormalName: nullable(ft.String),
    IsDiscontinued: nullable(ft.Boolean),
    IsObsolete: nullable(ft.Boolean),
    IsDetectionReliable: nullable(ft.Boolean),
  },
)
export type OcmConnectionType = ft.Static<typeof OcmConnectionType>

/** One row of `CurrentTypes`: AC single phase, AC three phase, DC. */
export const OcmCurrentType = objectWith(
  {
    ID: ft.Number,
  },
  {
    Title: nullable(ft.String),
    Description: nullable(ft.String),
  },
)
export type OcmCurrentType = ft.Static<typeof OcmCurrentType>

/** One row of `StatusTypes`: whether a POI or a single connection is live. */
export const OcmStatusType = objectWith(
  {
    ID: ft.Number,
  },
  {
    Title: nullable(ft.String),
    IsOperational: nullable(ft.Boolean),
    IsUserSelectable: nullable(ft.Boolean),
  },
)
export type OcmStatusType = ft.Static<typeof OcmStatusType>

/**
 * One row of `ChargerTypes`, which is what `ConnectionInfo.Level` points at:
 * OCM's coarse level 1 / 2 / 3 banding.
 */
export const OcmChargerType = objectWith(
  {
    ID: ft.Number,
  },
  {
    Title: nullable(ft.String),
    Comments: nullable(ft.String),
    IsFastChargeCapable: nullable(ft.Boolean),
  },
)
export type OcmChargerType = ft.Static<typeof OcmChargerType>

/** One row of `Operators`. The only place OCM publishes an operator *name*. */
export const OcmOperatorInfo = objectWith(
  {
    ID: ft.Number,
  },
  {
    Title: nullable(ft.String),
    WebsiteURL: nullable(ft.String),
    PhonePrimaryContact: nullable(ft.String),
    ContactEmail: nullable(ft.String),
    IsPrivateIndividual: nullable(ft.Boolean),
    Comments: nullable(ft.String),
  },
)
export type OcmOperatorInfo = ft.Static<typeof OcmOperatorInfo>

export const OcmUsageType = objectWith(
  {
    ID: ft.Number,
  },
  {
    Title: nullable(ft.String),
    IsPayAtLocation: nullable(ft.Boolean),
    IsMembershipRequired: nullable(ft.Boolean),
    IsAccessKeyRequired: nullable(ft.Boolean),
  },
)
export type OcmUsageType = ft.Static<typeof OcmUsageType>

export const OcmCountry = objectWith(
  {
    ID: ft.Number,
  },
  {
    Title: nullable(ft.String),
    ISOCode: nullable(ft.String),
    ContinentCode: nullable(ft.String),
  },
)
export type OcmCountry = ft.Static<typeof OcmCountry>

export const OcmDataProvider = objectWith(
  {
    ID: ft.Number,
  },
  {
    Title: nullable(ft.String),
    WebsiteURL: nullable(ft.String),
    /** The licence the provider's rows are published under. Attribution input. */
    License: nullable(ft.String),
    IsOpenDataLicensed: nullable(ft.Boolean),
  },
)
export type OcmDataProvider = ft.Static<typeof OcmDataProvider>

/**
 * `/v3/referencedata`, narrowed to the lists the pipeline reads.
 *
 * Every list is optional: the endpoint is one big object and OCM adds to it,
 * so a caller that only needs `ConnectionTypes` must not fail because some
 * other list changed shape.
 */
export const OcmReferenceData = objectWith(
  {},
  {
    ConnectionTypes: ft.Array(OcmConnectionType),
    CurrentTypes: ft.Array(OcmCurrentType),
    StatusTypes: ft.Array(OcmStatusType),
    ChargerTypes: ft.Array(OcmChargerType),
    Operators: ft.Array(OcmOperatorInfo),
    UsageTypes: ft.Array(OcmUsageType),
    Countries: ft.Array(OcmCountry),
    DataProviders: ft.Array(OcmDataProvider),
  },
)
export type OcmReferenceData = ft.Static<typeof OcmReferenceData>

// ---------------------------------------------------------------------------
// POI
// ---------------------------------------------------------------------------

export const OcmAddressInfo = objectWith(
  {
    Latitude,
    Longitude,
  },
  {
    ID: nullable(ft.Number),
    Title: nullable(ft.String),
    AddressLine1: nullable(ft.String),
    AddressLine2: nullable(ft.String),
    Town: nullable(ft.String),
    StateOrProvince: nullable(ft.String),
    Postcode: nullable(ft.String),
    CountryID: nullable(ft.Number),
  },
)
export type OcmAddressInfo = ft.Static<typeof OcmAddressInfo>

/**
 * OCM's `ConnectionInfo`, mirrored field for field.
 *
 * The `*ID` fields and their expanded objects are both present because OCM
 * populates the objects only with `compact=false`; a cached dump taken the
 * other way still has to parse, and the id is what the merge falls back to.
 */
export const OcmConnection = objectWith(
  {},
  {
    ID: nullable(ft.Number),
    /** OCM's own connector taxonomy id. Not EIPA's. */
    ConnectionTypeID: nullable(ft.Number),
    /** The expanded `ConnectionTypeID`, present with `compact=false`. */
    ConnectionType: nullable(OcmConnectionType),
    /** The operator's own reference for this connection, free text. */
    Reference: nullable(ft.String),
    StatusTypeID: nullable(ft.Number),
    StatusType: nullable(OcmStatusType),
    LevelID: nullable(ft.Number),
    Level: nullable(OcmChargerType),
    Amps: nullable(ft.Number),
    /** The reason OCM is in the pipeline at all. */
    Voltage: nullable(ft.Number),
    PowerKW: nullable(ft.Number),
    /** 10 AC single phase, 20 AC three phase, 30 DC. */
    CurrentTypeID: nullable(ft.Number),
    CurrentType: nullable(OcmCurrentType),
    Quantity: nullable(ft.Number),
    Comments: nullable(ft.String),
  },
)
export type OcmConnection = ft.Static<typeof OcmConnection>

export const OcmPoi = objectWith(
  {
    ID: ft.Number,
    AddressInfo: OcmAddressInfo,
  },
  {
    UUID: nullable(ft.String),
    DataProviderID: nullable(ft.Number),
    OperatorID: nullable(ft.Number),
    /** The expanded `OperatorID`, present with `compact=false`. Carries the name. */
    OperatorInfo: nullable(OcmOperatorInfo),
    UsageTypeID: nullable(ft.Number),
    StatusTypeID: nullable(ft.Number),
    NumberOfPoints: nullable(ft.Number),
    UsageCost: nullable(ft.String),
    DateLastVerified: nullable(ft.String),
    DateLastStatusUpdate: nullable(ft.String),
    Connections: nullable(ft.Array(OcmConnection)),
  },
)
export type OcmPoi = ft.Static<typeof OcmPoi>

/** `poi.json` is a bare array - OCM publishes no envelope. */
export const OcmPoiFile = ft.Array(OcmPoi)
export type OcmPoiFile = ft.Static<typeof OcmPoiFile>

/** OCM `CurrentTypeID` values, for reading `OcmConnection.CurrentTypeID`. */
export const OCM_CURRENT_TYPE = {
  acSinglePhase: 10,
  acThreePhase: 20,
  dc: 30,
} as const

/**
 * Attribution the licences oblige us to publish wherever OCM data is shown.
 *
 * Contributed Open Charge Map data is CC BY 4.0; the Polish rows that reach us
 * through OCM's import of open registries carry ODbL, which additionally
 * requires the share-alike notice. Rendering this belongs to the frontend - it
 * is recorded here so the obligation travels with the data model rather than
 * living in one component's copy deck.
 */
export const OCM_ATTRIBUTION = {
  source: 'Open Charge Map',
  url: 'https://openchargemap.org',
  /** CC BY 4.0 covers the contributed database as published by OCM. */
  license: 'CC BY 4.0',
  licenseUrl: 'https://creativecommons.org/licenses/by/4.0/',
  /** Rows imported from open registries (Poland included) stay ODbL. */
  odblNotice: 'Contains data from Open Charge Map contributors, ODbL 1.0',
  odblUrl: 'https://opendatacommons.org/licenses/odbl/1-0/',
} as const
